/**
 * Overmind Compatibility Layer
 *
 * Provides useAppState, useActions, and useEffects hooks that map to the custom store.
 * This allows gradual migration from Overmind patterns to direct store usage.
 *
 * Usage (existing pattern - still works):
 *   const { myUserId, contactsById } = useAppState();
 *   const { setUser } = useActions();
 *
 * Recommended new pattern:
 *   import { useStore } from '../store';
 *   const myUserId = useStore(state => state.apiToken ? parseJwt(state.apiToken).user_id : undefined);
 *   const setUser = useStore(state => state.setUser);
 */

import { useMemo } from "react";
import { useStore } from "./index";
import { parseJwt } from "./slices/auth";
import { services } from "@/services/module_api";
import { callRoomIdFromThreadKey, resolveCallRoomId, fetchCallToken } from "@/services/call";
import { appConfig, resetInitState, runBootstrap } from "@/app/module_api";
import { api as coreApi, crypto as coreCrypto, initSwitchCore, profile as coreProfile, recovery as coreRecovery, groups as coreGroups, HiLoState, ThreadType, WebSocketEventType, setToken, initializeWebSocket, disconnectWebSocket, generateUUID, stringifyThread, decodeBase64 } from "@/core_bridge/module_api";
import { createDirectThreadKey } from "@/core_bridge/thread_keys";
import { cacheDecryptedPhoto, buildDisplayPhotoFanoutPayload } from "@/services/module_api";
import { Member, Thread } from "@/data/schemas";
import { extractContactPublicKeys, resolveContactPublicKey } from "@/data/adapters/contact_keys";
import { addMember as groupAddMember, removeMember as groupRemoveMember } from "@/features/groups/module_api";
import { stateServices } from "@/state/module_api";
import {
  inviteCommunityMember,
  removeCommunityMember,
  updateCommunityMemberRole,
  leaveCommunity as leaveCommunityFlow,
} from "@/features/community/communityService";
import { performClientLogout } from "@/state/logout";
import { buildBasicAuthorizationHeader } from "@/utils/auth";
import { send as sendWebSocket } from "@/transport/webSocketService";

// Stable empty object/array references to avoid creating new objects in selectors
// This prevents infinite re-render loops with Object.is comparison
const EMPTY_OBJ: Record<string, any> = {};
const EMPTY_ARR: any[] = [];
const EMPTY_SELECTION: Record<string, any> = {};

/**
 * Compatibility hook for useAppState
 * Returns a flat object with all state and derived values
 * Uses useMemo to prevent creating new objects on every render
 */
export function useAppState() {
  // Get raw state values (primitives and references)
  const apiToken = useStore((state) => state.apiToken);
  const callToken = useStore((state) => state.callToken);
  const communityToken = useStore((state) => state.communityToken);
  const jitsiToken = useStore((state) => state.jitsiToken);
  const activeTab = useStore((state) => state.activeTab);
  const isRootPageInit = useStore((state) => state.isRootPageInit);
  const myNumber = useStore((state) => state.myNumber);
  const myProfilePhoto = useStore((state) => state.myProfilePhoto);
  const profilePhotos = useStore((state) => (state as any).profilePhotos || {});
  const isSearchable = useStore((state) => state.isSearchable);
  const iosPushToken = useStore((state) => state.iosPushToken);
  const shareExtensionPayload = useStore((state) => state.shareExtensionPayload);
  const hasCodeBeenSet = useStore((state) => state.hasCodeBeenSet);
  const nativeProcessedMessages = useStore((state) => state.nativeProcessedMessages);
  const profileRenderRef = useStore((state) => state.profileRenderRef);

  // User state
  const contactsById = useStore((state) => state.contactsById);

  // Group state
  const threadMap = useStore((state) => state.threadMap);

  // Hilo state
  const hi = useStore((state) => state.hi);
  const lo = useStore((state) => state.lo);
  const destroy = useStore((state) => state.destroy);
  const environment = useStore((state) => state.environment);
  const isLocked = useStore((state) => state.isLocked);
  const isConfigured = useStore((state) => state.isConfigured);

  // Encryption state
  const keyPair = useStore((state) => state.keyPair);
  const displayPhotoKey = useStore((state) => state.displayPhotoKey);
  const coverPhotoKey = useStore((state) => state.coverPhotoKey);
  const profileKey = useStore((state) => state.profileKey);

  // Thread state - use stable empty references to avoid infinite re-renders
  const threadSelection = useStore((state) => (state as any).selection ?? EMPTY_SELECTION);
  const selectedThreadId = useStore((state) => (state as any).selectedThreadId);
  const messagesByThread = useStore((state) => (state as any).messagesByThread ?? EMPTY_OBJ);
  const threadSearch = useStore((state) => state.threadSearch);

  // Derive thread messages with useMemo to avoid selector returning new arrays
  const threadMessages = useMemo(() => {
    const sel = threadSelection;
    const tid = sel.threadKey || sel.groupId || sel.withUserId || selectedThreadId;
    if (!tid) return EMPTY_ARR;
    return messagesByThread[tid] ?? EMPTY_ARR;
  }, [threadSelection, selectedThreadId, messagesByThread]);

  // Community state
  const communityMap = useStore((state) => state.communityMap);
  const selectedCommunityCursor = useStore((state) => state.selectedCommunityCursor);

  // Recovery state
  const mnemonic = useStore((state) => state.mnemonic);
  const authKeyPair = useStore((state) => state.authKeyPair);
  const isSetupComplete = useStore((state) => state.isSetupComplete);
  const isRecovering = useStore((state) => state.isRecovering);
  const recoveryStep = useStore((state) => state.recoveryStep);
  const recoveryError = useStore((state) => state.recoveryError);
  const encryptedData = useStore((state) => state.encryptedData);
  const isDataRestored = useStore((state) => state.isDataRestored);
  const setupStep = useStore((state) => state.setupStep);
  const verificationWords = useStore((state) => state.verificationWords);
  const isAuthKeyRegistered = useStore((state) => state.isAuthKeyRegistered);
  const registrationError = useStore((state) => state.registrationError);

  // NatsChat state
  const natsChatMessages = useStore((state) => state.natsChatMessages);
  const userCommunities = useStore((state) => state.userCommunities);
  const natsChatSelectedCommunity = useStore((state) => state.natsChatSelectedCommunity);
  const natsChatSelectedGroup = useStore((state) => state.natsChatSelectedGroup);
  const communityGroups = useStore((state) => state.communityGroups);
  const userGroups = useStore((state) => state.userGroups);
  const communityMembers = useStore((state) => state.communityMembers);
  const groupMembers = useStore((state) => state.groupMembers);
  const wsConnected = useStore((state) => state.wsConnected);

  // Credit state
  const creditHistoryOnMonth = useStore((state) => state.creditHistoryOnMonth);
  const balance = useStore((state) => state.balance);
  const iap = useStore((state) => state.iap);

  // LLM state
  const llmInput = useStore((state) => state.llmInput);
  const llmMessages = useStore((state) => state.llmMessages);
  const llmWaiting = useStore((state) => state.llmWaiting);

  // Memoize derived values from JWT
  const tokenData = useMemo(() => {
    return apiToken ? parseJwt(apiToken) : {};
  }, [apiToken]);

  // Memoize user namespace
  const user = useMemo(() => {
    const me =
      tokenData.user_id && contactsById[tokenData.user_id]
        ? contactsById[tokenData.user_id]
        : tokenData.user_id && (profilePhotos as any)[tokenData.user_id]
          ? ({
              userId: tokenData.user_id,
              displayPhoto: (profilePhotos as any)[tokenData.user_id].url,
            } as any)
          : tokenData.user_id && myProfilePhoto
            ? ({ userId: tokenData.user_id, displayPhoto: myProfilePhoto } as any)
            : undefined;

    return {
      contactsById,
      keyMap: Object.values(contactsById)
        .flatMap((u) => extractContactPublicKeys(u))
        .reduce<Record<string, string>>((map, { deviceId, publicKey }) => {
          map[deviceId] = publicKey;
          return map;
        }, {}),
      usersList: Object.values(contactsById),
      me,
    };
  }, [contactsById, myProfilePhoto, tokenData.user_id, profilePhotos]);

  // Memoize group namespace
  const group = useMemo(() => ({
    threadMap,
    threadsList: Object.values(threadMap).map((thread) => {
      if (environment === "hi") {
        return {
          ...thread,
          lastMessage: thread.hiloMeta?.lastMessage ?? thread.lastMessage,
          updatedAt: thread.hiloMeta?.lastMessageAt ?? thread.updatedAt,
        };
      }
      return thread;
    }),
  }), [threadMap, environment]);

  // Memoize thread namespace
  const thread = useMemo(() => {
    const selection = threadSelection || {};
    const threadKey =
      selection.threadKey ||
      selection.groupId ||
      selection.withUserId ||
      selectedThreadId;
    const threadMeta =
      threadKey && selection.groupId ? threadMap[selection.groupId] : undefined;
    const messages = threadMessages;

    let disableInput: { status: boolean; reason: string } | boolean = false;
    if (threadMeta && "membershipStatus" in threadMeta &&
      (threadMeta as any).membershipStatus?.status === "exited") {
      disableInput = { status: true, reason: "You have exited this group." };
    } else if (threadMeta && (threadMeta as any).isAnnouncementGroup) {
      const community = (threadMeta as any).communityId
        ? communityMap[(threadMeta as any).communityId]
        : undefined;
      const canPost = community
        ? (community as any).members?.some((m: any) =>
            m.userId === tokenData.user_id && (m.role === "owner" || m.role === "admin"))
        : (threadMeta as any).ownerId === tokenData.user_id;
      if (!canPost) {
        disableInput = { status: true, reason: "Only owner or admin can broadcast." };
      }
    }

    return {
      selectedThread: {
        ...selection,
        threadKey: threadKey,
        receiverId: selection.receiverId || selection.withUserId || threadKey,
        senderId: selection.senderId,
        messages,
        thread: threadMeta,
        disableInput,
      },
      search: threadSearch ?? { query: "", results: [], isSearching: false, error: undefined },
      isWebviewActiveInThreadView: !!(
        selection.receiverId ||
        selection.senderId ||
        selection.groupId ||
        threadKey
      ),
    };
  }, [threadSelection, selectedThreadId, threadMessages, threadMap, tokenData.user_id, threadSearch]);

  // Memoize hilo namespace
  const hilo = useMemo(() => ({
    hi,
    lo,
    destroy,
    environment,
    isLocked,
    isConfigured,
  }), [hi, lo, destroy, environment, isLocked, isConfigured]);

  // Memoize encryption namespace
  const encryption = useMemo(() => ({
    keyPair,
    displayPhotoKey,
    coverPhotoKey,
    profileKey,
  }), [keyPair, displayPhotoKey, coverPhotoKey, profileKey]);

  // Memoize community namespace
  const community = useMemo(() => ({
    communityMap,
    communityList: Object.values(communityMap).sort((a, b) =>
      a.name.localeCompare(b.name)
    ),
    selectedCommunityCursor,
    selectedCommunity: selectedCommunityCursor
      ? communityMap[selectedCommunityCursor] || null
      : null,
  }), [communityMap, selectedCommunityCursor]);

  // Memoize recovery namespace
  const recovery = useMemo(() => ({
    mnemonic,
    authKeyPair,
    isSetupComplete,
    isRecovering,
    recoveryStep,
    error: recoveryError,
    encryptedData,
    isDataRestored,
    setupStep,
    verificationWords,
    isAuthKeyRegistered,
    registrationError,
  }), [mnemonic, authKeyPair, isSetupComplete, isRecovering, recoveryStep, recoveryError,
    encryptedData, isDataRestored, setupStep, verificationWords, isAuthKeyRegistered, registrationError]);

  // Memoize credit namespace
  const credit = useMemo(() => ({
    creditHistoryOnMonth,
    balance,
    iap,
  }), [creditHistoryOnMonth, balance, iap]);

  // Memoize llm namespace
  const llm = useMemo(() => ({
    input: llmInput,
    messages: llmMessages,
    waiting: llmWaiting,
  }), [llmInput, llmMessages, llmWaiting]);

  // Return the full state object
  return {
    // Auth state
    apiToken,
    apiTokenObject: tokenData,
    callToken,
    communityToken,
    jitsiToken,
    activeTab,
    isRootPageInit,
    myNumber,
    myProfilePhoto,
    isSearchable,
    iosPushToken,
    shareExtensionPayload,
    hasCodeBeenSet,
    nativeProcessedMessages,
    profileRenderRef,

    // Derived from JWT
    myUserId: tokenData.user_id,
    myDeviceId: tokenData.device_id,
    myUsername: tokenData.username,
    myDiscriminator: tokenData.discriminator,
    myHandle: tokenData.full_handle || tokenData.username || tokenData.user_id,
    myUser: tokenData.user_id ? (contactsById[tokenData.user_id] || (user as any).me) : undefined,
    tokenType: tokenData.type,
    appId: tokenData.app_id,
    myPrimaryDeviceId: tokenData.primary_device_id || tokenData.device_id,
    isPrimaryDevice: tokenData.is_primary_device || false,
    isRegistered: !!tokenData.device_id,
    myNumberPrefixed: myNumber ? `+${myNumber}` : undefined,
    hasLinkedDevice: !!tokenData.linked_device1,
    linkedDeviceDeviceId: tokenData.linked_device1?.device_id,
    rootTitle: activeTab.split("/")[1]?.split("-")[0]?.charAt(0).toUpperCase() +
      (activeTab.split("/")[1]?.split("-")[0]?.slice(1) || ""),

    // Namespaced state
    user,
    group,
    thread,
    hilo,
    encryption,
    community,
    recovery,
    credit,
    llm,

    // WebSocket connection status (flat for convenience)
    socketConnected: wsConnected,
  };
}

/**
 * Compatibility hook for useActions
 * Returns all action functions from the store
 *
 * IMPORTANT: We use useStore.getState() instead of useStore() to avoid subscribing
 * to state changes. Actions are stable functions that don't change, so we don't need
 * reactivity here. Using useStore() without a selector caused infinite re-render loops.
 */
export function useActions() {
  // Get actions from the store without subscribing to state changes
  // This prevents infinite re-render loops caused by state subscriptions
  return useMemo(() => {
    const store = useStore.getState();

    return {
    // Auth actions
    setApiToken: store.setApiToken,
    setCallToken: store.setCallToken,
    setCommunityToken: store.setCommunityToken,
    setJitsiToken: store.setJitsiToken,
    setActiveTab: store.setActiveTab,
    setIsRootPageInit: store.setIsRootPageInit,
    setMyNumber: store.setMyNumber,
    setMyProfilePhoto: store.setMyProfilePhoto,
    setIsSearchable: store.setIsSearchable,
    setIosPushToken: store.setIosPushToken,
    setShareExtensionPayload: store.setShareExtensionPayload,
    setHasCodeBeenSet: store.setHasCodeBeenSet,
    addNativeProcessedMessage: store.addNativeProcessedMessage,
    clearNativeProcessedMessages: store.clearNativeProcessedMessages,
    incrementProfileRenderRef: store.incrementProfileRenderRef,

    // User actions (namespaced)
    user: {
      setUser: store.setUser,
      setUsers: store.setUsers,
      updateUser: store.updateUser,
      removeUser: store.removeUser,
      clearUsers: store.clearUsers,
      findUser: async ({ handle }: { handle: string }) => {
        const result = await coreApi.findUser(handle);
        if (!(result as any)?.user) {
          throw new Error("User not found");
        }
        return {
          user: (result as any).user,
          userId: (result as any).userId,
          discriminator: (result as any).discriminator,
        };
      },
      addContact: async ({ user }: { user: any }) => {
        // Ensure all required Contact fields have defaults
        const userWithDefaults = {
          ...user,
          isBlocked: (user as any).isBlocked ?? (user as any).isBlock ?? false,
          isMuted: (user as any).isMuted ?? (user as any).isMute ?? false,
          type: user.type ?? "user",
        };
        // If the user has an encrypted display photo but we have no key, drop it to avoid failed decrypts
        if (
          userWithDefaults.displayPhoto &&
          userWithDefaults.displayPhoto.endsWith(".enc") &&
          !(userWithDefaults as any).displayPhotoKey
        ) {
          userWithDefaults.displayPhoto = undefined;
        }
        store.setUser(userWithDefaults);
        await services.db.insertUser(userWithDefaults);

        const myUserId = parseJwt(store.apiToken || "").user_id;
        const theirPublicKey = resolveContactPublicKey(userWithDefaults);
        if (!myUserId) {
          throw new Error("No user logged in");
        }
        if (!userWithDefaults.userId) {
          throw new Error("Added contact is missing userId");
        }
        if (!theirPublicKey) {
          throw new Error("Added contact is missing public key material");
        }

        const persisted = (await services.db.getUsers?.())?.find(
          (candidate: any) => candidate?.userId === userWithDefaults.userId
        );
        if (!persisted && !store.contactsById?.[userWithDefaults.userId]) {
          throw new Error("Added contact is not visible in local contact store");
        }

        const threadId = createDirectThreadKey(myUserId, userWithDefaults.userId);
        const existingThread = await services.db.getGrp?.(threadId);
        if (!existingThread) {
          const now = Date.now();
          const thread = {
            id: threadId,
            name:
              userWithDefaults.fullHandle ||
              userWithDefaults.username ||
              userWithDefaults.userId,
            description: null,
            members: [],
            lastMessage: null,
            ownerId: myUserId,
            withUserId: userWithDefaults.userId,
            createdAt: now,
            updatedAt: now,
            unread: 0,
            isAnnouncementGroup: false,
            isDefaultGroup: false,
            isStandaloneGroup: false,
            type: myUserId === userWithDefaults.userId ? ThreadType.SELF : ThreadType.DM,
          };
          await services.db.insertGrp?.(thread as any);
          store.setThread?.(thread as any);
        } else {
          store.setThread?.(existingThread as any);
        }

        if (!(await services.db.getGrp?.(threadId))) {
          throw new Error("Canonical DM thread was not created for added contact");
        }

        // Fan out display photo key/url to the newly added contact
        try {
          const displayPhotoUrl =
            store.contactsById[myUserId]?.displayPhoto ||
            (store as any).profilePhotos?.[myUserId]?.url ||
            store.myProfilePhoto;
          const displayPhotoKey = store.displayPhotoKey;

          if (myUserId && displayPhotoUrl && displayPhotoKey && theirPublicKey) {
            await initSwitchCore();
            const prepared = (coreApi as any).prepareContactAddMessage(
              myUserId,
              displayPhotoUrl,
              displayPhotoKey,
              userWithDefaults.userId,
              theirPublicKey,
              myUserId,
              store.keyPair?.base64?.secretKey || (store.keyPair?.base64 as any)?.secret_key || ""
            );
            try {
              await services.threadService.sendMessage?.(prepared);
            } catch (err) {
              console.warn("[compat/user] failed to send contact-add profile update", err);
            }
          }
        } catch (err) {
          console.warn("[compat/user] contact-add fanout skipped", err);
        }
      },
      blockUserNew: async ({ user }: { user: any }) => {
        const updated = { ...user, isBlocked: true };
        store.setUser(updated);
        await services.db.updateUser(user.userId, updated);
      },
      unblockUserNew: async ({ user }: { user: any }) => {
        const updated = { ...user, isBlocked: false };
        store.setUser(updated);
        await services.db.updateUser(user.userId, updated);
      },
      reportUserNew: async ({ user, reason, reasonCategory }: { user: any; reason?: string; reasonCategory?: string }) => {
        console.log("[compat/user] reportUserNew: Reporting user", user, "Reason:", reason, "Category:", reasonCategory);

        try {
          const apiToken = store.apiToken;
          if (!apiToken) {
            throw new Error("Not authenticated");
          }

          const response = await fetch(`${appConfig.apiUrl}/report-user`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: buildBasicAuthorizationHeader(apiToken),
            },
            body: JSON.stringify({
              appId: appConfig.appId,
              reportedUserId: user.userId,
              reasonCategory: reasonCategory || "other",
              description: reason || "",
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to report user: ${errorText}`);
          }

          const result = await response.json();
          console.log("[compat/user] reportUserNew: Report submitted successfully", result);
          return { success: true, reportId: result.reportId };
        } catch (error) {
          console.error("[compat/user] reportUserNew error:", error);
          throw error;
        }
      },
      deleteUser: async ({ user }: { user: any }) => {
        store.removeUser(user.userId);
        await (services.db as any).deleteUser?.(user.userId);
      },
      handleDeleteMyDisplayPhoto: async () => {
        const myUserId = parseJwt(store.apiToken || "").user_id;
        if (!myUserId) return;
        try {
          const profileEntry = (store as any).getProfilePhoto?.(myUserId);
          const displayPhotoUrl =
            store.contactsById[myUserId]?.displayPhoto || profileEntry?.url;
          try {
            await coreApi.removeObject(
              `displayphoto/${myUserId}/displayphoto-${myUserId}.enc`
            );
          } catch (err) {
            console.warn("[compat/user] removeObject via core failed", err);
          }
          const currentUser = store.contactsById[myUserId];
          if (currentUser) {
            store.setUser({ ...currentUser, displayPhoto: "" });
          }
          (store as any).removeProfilePhoto?.(myUserId);
          store.setMyProfilePhoto?.(undefined as any);
          store.setDisplayPhotoKey(undefined);
          await services.db.saveDisplayPhotoKey?.("");
          await services.db.removeProfilePhotoEntry?.(myUserId);
          try {
            const { updateProfile } = await import("@/services/module_api");
            await updateProfile(
              JSON.stringify({
                userId: myUserId,
                displayPhoto: "",
              })
            );
            try {
              localStorage.removeItem("displayPhotoUrl");
            } catch {
              // ignore
            }
          } catch (err) {
            console.warn("[compat/user] failed to clear profile json", err);
          }
          // Clear cached files
          try {
            await services.filesystem.deleteFile?.(`profilephoto/${myUserId}/decrypted`);
          } catch (err) {
            console.warn("[compat/user] failed to delete cached profile photo", err);
          }
          try {
            if (displayPhotoUrl) {
              await services.filesystem.deleteFile?.(`${displayPhotoUrl}:decrypted`);
            }
          } catch (err) {
            // ignore cache miss
          }
          store.incrementProfileRenderRef();
        } catch (error) {
          console.error("[compat/user] handleDeleteMyDisplayPhoto error:", error);
        }
      },
    },

    // Group actions (namespaced)
    group: {
      setGroup: store.setGroup,
      setGroups: store.setGroups,
      updateGroup: store.updateGroup,
      removeGroup: store.removeGroup,
      clearGroups: store.clearGroups,
      // Delegated to features/groups/GroupService (cross-platform parity)
      addGroupMember: async ({ member, group }: { member: Member; group: Thread }) => {
        await groupAddMember(member, group);
      },
      deleteMember: async ({ groupId, userId }: { groupId: string; userId: string }) => {
        await groupRemoveMember(groupId, userId);
      },
    },

    // Thread actions (namespaced)
    thread: {
      selectThread: async ({
        threadKey,
        receiverId,
        senderId,
        groupId,
        title,
      }: {
        threadKey?: string;
        receiverId?: string;
        senderId?: string;
        groupId?: string;
        title?: string;
      } = {}) => {
        const id =
          threadKey ||
          groupId ||
          receiverId ||
          senderId ||
          undefined;
        if ((store as any).setActiveTab) {
          (store as any).setActiveTab("/threads-tab");
        }
        if ((store as any).selectThread) {
          (store as any).selectThread({
            threadKey: id,
            receiverId,
            senderId,
            groupId,
            title,
          });
        }
        return;
      },
      exitThread: () => {
        if ((store as any).clearSearch) {
          (store as any).clearSearch();
        }
      },
      setSelectedThread: store.setSelectedThread,
      clearSelectedThread: store.clearSelectedThread,
      setThreadMessages: store.setThreadMessages,
      addThreadMessage: store.addThreadMessage,
      addThreadMessages: store.addThreadMessages,
      updateThreadMessage: store.updateThreadMessage,
      removeThreadMessage: store.removeThreadMessage,
      setThreadLoading: store.setThreadLoading,
      setThreadError: store.setThreadError,
      setHasMoreOlderMessages: store.setHasMoreOlderMessages,
      setSearchQuery: store.setSearchQuery,
      setSearchResults: store.setSearchResults,
      setIsSearching: store.setIsSearching,
      setSearchError: store.setSearchError,
      clearSearch: () => {
        // Clear search state - store.clearSearch may not exist
        if ((store as any).clearSearch) {
          (store as any).clearSearch();
        } else if ((store as any).setSearchQuery) {
          (store as any).setSearchQuery("");
          (store as any).setSearchResults?.([]);
          (store as any).setIsSearching?.(false);
          (store as any).setSearchError?.(undefined);
        }
      },
      searchMessages: async ({ query }: { query: string }) => {
        console.log("[compat/thread] searchMessages", query);
        const currentStore = useStore.getState() as any;
        const threadKey = currentStore.selectedThreadId || currentStore.selection?.threadKey;

        currentStore.setSearchQuery?.(query);
        currentStore.setIsSearching?.(true);
        currentStore.setSearchError?.(undefined);

        if (!query || query.trim().length === 0) {
          currentStore.setSearchResults?.([]);
          currentStore.setIsSearching?.(false);
          return;
        }

        try {
          const { db: storage } = await import("@/services/module_api");
          const results = await storage.searchMessages(query, threadKey, 50);
          console.log("[compat/thread] search results", results?.length || 0);
          currentStore.setSearchResults?.(results || []);
        } catch (e) {
          console.error("[compat/thread] search error", e);
          currentStore.setSearchError?.((e as Error).message);
        } finally {
          currentStore.setIsSearching?.(false);
        }
      },
      sendMessage: async (message: any) => {
        const threadKey =
          (store as any).selection?.threadKey ||
          message.groupId ||
          message.receiverId ||
          message.senderId;

        // Broadcast over thread service if available
        try {
          if ((services as any).threadService?.sendMessage) {
            (services as any).threadService.sendMessage(message);
          } else {
            // Optimistically add to UI if no thread service
            if ((store as any).appendMessage && threadKey) {
              (store as any).appendMessage(threadKey, message);
            } else if ((store as any).addThreadMessage) {
              (store as any).addThreadMessage(message);
            }
          }
        } catch (err) {
          console.warn("[compat/thread] sendMessage failed", err);
        }
      },
      loadOlderMessages: async () => {
        // No-op for now (core pagination not wired)
        return;
      },
      deleteMessage: async (msg: any) => {
        const messageId = msg?.id || msg;
        // Get fresh store state for threadKey
        const currentStore = useStore.getState() as any;
        const threadKey = msg?.threadKey || msg?.threadKey || currentStore.selectedThreadId || currentStore.selection?.threadKey;

        console.log("[compat/thread] deleteMessage", { messageId, threadKey, msg });
        if (!messageId) return;

        // Remove from UI using thread slice
        if (currentStore.removeMessage && threadKey) {
          console.log("[compat/thread] calling removeMessage", threadKey, messageId);
          currentStore.removeMessage(threadKey, messageId);
        }

        // Also try old thread slice
        if (currentStore.removeThreadMessage) {
          currentStore.removeThreadMessage(messageId);
        }

        // Delete from storage
        try {
          const { db: storage } = await import("@/services/module_api");
          await storage.deleteMessage(messageId);
          console.log("[compat/thread] deleted from storage", messageId);
        } catch (e) {
          console.error("[compat/thread] Failed to delete from storage:", e);
        }
      },
      joinCall: async (roomNameOrMessage?: string | { roomId?: string; threadKey?: string }) => {
        const currentStore = useStore.getState() as any;
        // Handle both string roomName and message object with roomId/threadKey
        let rawRoomId: string | undefined;
        if (typeof roomNameOrMessage === "string") {
          rawRoomId = roomNameOrMessage;
        } else if (roomNameOrMessage) {
          rawRoomId = roomNameOrMessage.roomId || roomNameOrMessage.threadKey;
        }
        const threadKey = rawRoomId || currentStore.selectedThreadId || currentStore.selection?.threadKey;
        if (!threadKey) {
          console.warn("[compat/thread] joinCall: no threadKey/roomId");
          return;
        }
        try {
          const { initSwitchCore } = await import("@/core_bridge/module_api");
          const { jitsi } = await import("@/services/module_api");
          // Ensure WASM is initialized
          await initSwitchCore();
          const callRoomId = resolveCallRoomId(threadKey);
          const tokenValue = currentStore.apiToken;
          if (!tokenValue) {
            console.error("[compat/thread] joinCall: missing auth token");
            return;
          }
          const result = await fetchCallToken(callRoomId, tokenValue);
          const token = result.token;
          if (!token) {
            console.error("[compat/thread] joinCall: no token returned");
            return;
          }
          if (!result.jitsiUrl) {
            console.error("[compat/thread] joinCall: no jitsiUrl returned by server");
            return;
          }
          console.log("[compat/thread] joinCall", { roomId: callRoomId, hasToken: !!token });
          jitsi.join({ roomId: callRoomId, token, serverUrl: result.jitsiUrl });
        } catch (err) {
          console.error("[compat/thread] joinCall failed:", err);
        }
      },
      startCall: async () => {
        const currentStore = useStore.getState() as any;
        const threadKey = currentStore.selectedThreadId || currentStore.selection?.threadKey;
        const receiverId = currentStore.selection?.receiverId;
        const myUserId = currentStore.myUserId;
        if (!threadKey) {
          console.warn("[compat/thread] startCall: no threadKey");
          return;
        }
        try {
          const { MessageType, initSwitchCore } = await import("@/core_bridge/module_api");
          const { jitsi } = await import("@/services/module_api");
          // Ensure WASM is initialized
          await initSwitchCore();
          const callRoomId = callRoomIdFromThreadKey(threadKey);

          // Send CALL message to notify the other user
          const callMessage = {
            id: `call-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: MessageType.CALL,
            senderId: myUserId,
            receiverId: receiverId,
            threadKey: threadKey,
            roomId: callRoomId,
            content: "",
            appId: (await import("@/app/module_api")).appConfig.appId,
            timestamp: Date.now(),
            createdAt: Date.now(),
          };
          console.log("[compat/thread] sending CALL message", callMessage);
          if ((services as any).threadService?.sendMessage) {
            (services as any).threadService.sendMessage(callMessage);
          }

          // Get Jitsi token and join
          const tokenValue = currentStore.apiToken;
          if (!tokenValue) {
            console.error("[compat/thread] startCall: missing auth token");
            return;
          }
          const result = await fetchCallToken(callRoomId, tokenValue);
          const token = result.token;
          if (!token) {
            console.error("[compat/thread] startCall: no token returned");
            return;
          }
          if (!result.jitsiUrl) {
            console.error("[compat/thread] startCall: no jitsiUrl returned by server");
            return;
          }
          console.log("[compat/thread] startCall joining Jitsi", { roomId: callRoomId, hasToken: !!token });
          jitsi.join({ roomId: callRoomId, token, serverUrl: result.jitsiUrl });
        } catch (err) {
          console.error("[compat/thread] startCall failed:", err);
        }
      },
      clearAllMessages: async () => {
        const currentStore = useStore.getState() as any;
        const threadKey = currentStore.selectedThreadId || currentStore.selection?.threadKey;

        if (!threadKey) {
          console.warn("[compat/thread] clearAllMessages: no threadKey");
          return;
        }

        console.log("[compat/thread] clearAllMessages", { threadKey });

        // Clear from UI
        if (currentStore.setMessages) {
          currentStore.setMessages(threadKey, []);
        }

        // Clear from storage
        try {
          const { db: storage } = await import("@/services/module_api");
          await storage.deleteMessagesForThread(threadKey);
          console.log("[compat/thread] cleared messages from storage");
        } catch (e) {
          console.error("[compat/thread] Failed to clear messages from storage:", e);
          throw e;
        }
      },
    },

    // Hilo actions (namespaced)
    hilo: {
      setHi: store.setHi,
      setLo: store.setLo,
      setDestroy: store.setDestroy,
      setEnvironment: store.setEnvironment,
      setCodes: store.setCodes,
      loadCodes: store.loadCodes,
      saveCodes: store.saveCodes,
      lock: store.lock,
      unlock: store.unlock,
      shouldLock: store.shouldLock,
      initHilo: async () => {
        store.loadCodes();
        console.log("[hilo] initHilo - loaded codes");
      },
      lockScreen: () => {
        store.lock();
        console.log("[hilo] lockScreen - screen locked");
      },
      activateLockScreen: () => {
        // Lock the screen if codes are configured
        const state = useStore.getState();
        if (state.isConfigured) {
          store.lock();
          console.log("[hilo] activateLockScreen - screen locked");
        } else {
          console.log("[hilo] activateLockScreen - codes not configured");
        }
      },
      getCodes: () => {
        store.loadCodes();
      },
    },

    // Encryption actions (namespaced)
    encryption: {
      setKeyPair: store.setKeyPair,
      setDisplayPhotoKey: store.setDisplayPhotoKey,
      setCoverPhotoKey: store.setCoverPhotoKey,
      setProfileKey: store.setProfileKey,
      generateSymmetricKey: () => {
        const key = coreCrypto.secretboxKeygen();
        // Convert Uint8Array to base64
        const base64 = btoa(String.fromCharCode(...key));
        return { key, base64 };
      },
    },

    // Profile photo actions (namespaced)
    profilePhoto: {
      setProfilePhoto: (entry: any) => (store as any).setProfilePhoto?.(entry),
      removeProfilePhoto: (userId: string) => (store as any).removeProfilePhoto?.(userId),
      getProfilePhoto: (userId: string) => (store as any).getProfilePhoto?.(userId),
    },

    // Community actions (namespaced)
    community: {
      setCommunity: store.setCommunity,
      setCommunities: store.setCommunities,
      updateCommunity: store.updateCommunity,
      removeCommunity: store.removeCommunity,
      clearCommunities: store.clearCommunities,
      setSelectedCommunityCursor: store.setSelectedCommunityCursor,
      leaveCommunity: async ({ community }: { community: any }) => {
        console.log("[community] leaveCommunity", community.id);
        try {
          await leaveCommunityFlow({ community });
        } catch (error: any) {
          console.error("[community] leaveCommunity error:", error);
          throw error;
        }
      },
      deleteCommunity: async ({ community }: { community: any }) => {
        console.log("[community] deleteCommunity", community.id);
        try {
          await coreApi.deleteCommunity(community.id);
          store.removeCommunity(community.id);
        } catch (error: any) {
          console.error("[community] deleteCommunity error:", error);
          throw error;
        }
      },
      joinCommunityGroup: async ({ group, community }: { group: any; community: any }) => {
        console.log("[community] joinCommunityGroup", { group, community });
        try {
          const result = await coreApi.joinCommunityGroup(community.id, group.id);
          if (result.group) {
            store.setGroup({
              ...result.group,
              id: result.group.group_id || group.id,
              type: "group" as ThreadType,
              communityId: community.id,
            });
          }
        } catch (error: any) {
          console.error("[community] joinCommunityGroup error:", error);
          throw error;
        }
      },
      addCommunityMember: async ({ member, community }: { member: any; community: any }) => {
        console.log("[community] addCommunityMember", { member, community });
        try {
          const communityId = community.communityId || community.id;
          const userId = member.userId || member.user_id;
          const role = member.role || "member";
          const currentCommunity = useStore.getState().communityMap[communityId];
          if (!currentCommunity) {
            throw new Error("Community not found in local state");
          }
          await inviteCommunityMember({ community: currentCommunity, userId, role });
        } catch (error: any) {
          console.error("[community] addCommunityMember error:", error);
          throw error;
        }
      },
      deleteCommunityMember: async ({ member, community }: { member: any; community: any }) => {
        console.log("[community] deleteCommunityMember", { member, community });
        try {
          const communityId = community.communityId || community.id;
          const userId = member.userId || member.user_id;
          const currentCommunity = useStore.getState().communityMap[communityId];
          if (!currentCommunity) {
            throw new Error("Community not found in local state");
          }
          await removeCommunityMember({ community: currentCommunity, userId });
        } catch (error: any) {
          console.error("[community] deleteCommunityMember error:", error);
          throw error;
        }
      },
      updateCommunityMemberRole: async ({ member, role, community }: { member: any; role: string; community: any }) => {
        console.log("[community] updateCommunityMemberRole", { member, role, community });
        try {
          const communityId = community.communityId || community.id;
          const userId = member.userId || member.user_id;
          const currentCommunity = useStore.getState().communityMap[communityId];
          if (!currentCommunity) {
            throw new Error("Community not found in local state");
          }
          await updateCommunityMemberRole({ community: currentCommunity, userId, role: role as any });
        } catch (error: any) {
          console.error("[community] updateCommunityMemberRole error:", error);
          throw error;
        }
      },
    },

    // Recovery actions (namespaced)
    recovery: {
      setMnemonic: store.setMnemonic,
      setAuthKeyPair: store.setAuthKeyPair,
      setIsSetupComplete: store.setIsSetupComplete,
      setIsRecovering: store.setIsRecovering,
      setRecoveryStep: store.setRecoveryStep,
      setRecoveryError: store.setRecoveryError,
      setEncryptedData: store.setEncryptedData,
      setIsDataRestored: store.setIsDataRestored,
      setSetupStep: store.setSetupStep,
      setVerificationWords: store.setVerificationWords,
      setIsAuthKeyRegistered: store.setIsAuthKeyRegistered,
      setRegistrationError: store.setRegistrationError,
      resetRecoveryState: store.resetRecoveryState,
      // Full action implementations for BackupPanel
      loadExistingMnemonic: () => {
        try {
          let storedMnemonic: string | null = null;
          try {
            storedMnemonic = stateServices.sessionState.getRecoveryPassphrase();
          } catch {
            storedMnemonic = null;
          }
          if (!storedMnemonic) {
            storedMnemonic = localStorage.getItem("account_mnemonic");
          }
          const storedKeypair = localStorage.getItem("auth_keypair");
          if (storedMnemonic && storedKeypair) {
            store.setMnemonic(storedMnemonic);
            store.setAuthKeyPair(JSON.parse(storedKeypair));
            store.setIsSetupComplete(true);
            store.setIsAuthKeyRegistered(true);
          }
        } catch (error) {
          console.error("[recovery] loadExistingMnemonic error:", error);
          store.setRecoveryError("Failed to load existing mnemonic");
        }
      },
      deleteMnemonic: () => {
        try {
          localStorage.removeItem("account_mnemonic");
          localStorage.removeItem("auth_keypair");
          try {
            stateServices.sessionState.setRecoveryPassphrase(null);
          } catch {
            // ignore
          }
          store.setMnemonic(null);
          store.setAuthKeyPair(null);
          store.setIsSetupComplete(false);
          store.setIsAuthKeyRegistered(false);
        } catch (error) {
          console.error("[recovery] deleteMnemonic error:", error);
          store.setRecoveryError("Failed to delete mnemonic");
        }
      },
      generateMnemonic: async () => {
        try {
          store.setSetupStep("generating");
          store.setRecoveryError(null);
          const { recovery: switchRecovery } = await import("@/core_bridge/module_api");
          const mnemonic = switchRecovery.generateMnemonic();
          const keyPair = switchRecovery.deriveKeypair(mnemonic);
          const encodeB64 = (bytes: Uint8Array): string => {
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
          };
          store.setMnemonic(mnemonic);
          store.setAuthKeyPair({
            publicKey: encodeB64(keyPair.public_key),
            privateKey: encodeB64((keyPair as any).secret_key || (keyPair as any).secretKey),
          });
          store.setSetupStep("displaying");
        } catch (error) {
          console.error("[recovery] generateMnemonic error:", error);
          store.setRecoveryError("Failed to generate mnemonic");
          // Cast to any since "error" is not in SetupStep union but used for error state
          store.setSetupStep("idle");
        }
      },
      selectVerificationWords: () => {
        const state = useStore.getState();
        if (!state.mnemonic) {
          store.setRecoveryError("No mnemonic available for verification");
          return;
        }
        store.setSetupStep("verifying");
      },
      verifyMnemonic: (confirmInput: string): boolean => {
        if (confirmInput.trim().toUpperCase() === "CONFIRM") {
          store.setSetupStep("saving");
          return true;
        } else {
          store.setRecoveryError("Please type CONFIRM to save your backup.");
          return false;
        }
      },
      completeSetup: async () => {
        try {
          const state = useStore.getState();
          if (!state.authKeyPair || !state.mnemonic) {
            throw new Error("No auth keypair or mnemonic available");
          }
          if (!state.apiToken) {
            throw new Error("Missing auth token");
          }
          const parsed = parseJwt(state.apiToken || "");
          const userId = parsed.user_id;
          const username = parsed.username;
          const discriminator = parsed.discriminator;
          const deviceId = parsed.device_id;
          if (!userId || !username || !discriminator || !deviceId) {
            throw new Error("Missing required identity fields");
          }
          if (!state.keyPair?.base64?.publicKey || !state.keyPair?.base64?.secretKey) {
            throw new Error("Missing encryption keypair");
          }
          store.setSetupStep("saving");

          const threads = Object.values((state as any).threadMap || {});
          const communities = Object.values((state as any).communityMap || {});

          const contactIdSet = new Set(
            Object.keys((state as any).contactsById || {}).filter(
              (id) => typeof id === "string" && id.length > 0
            )
          );

          for (const thread of threads as any[]) {
            if (!thread || typeof thread !== "object") continue;

            const withUserId =
              typeof thread.withUserID === "string"
                ? thread.withUserID
                : typeof thread.withUserId === "string"
                  ? thread.withUserId
                  : "";
            if (withUserId) {
              contactIdSet.add(withUserId);
            }

            const memberIds = Array.isArray(thread.memberIds)
              ? thread.memberIds
              : Array.isArray(thread.members)
                ? thread.members.map((member: any) =>
                    typeof member === "string"
                      ? member
                      : member?.userId || member?.userID || member?.user_id
                  )
                : [];

            for (const memberId of memberIds) {
              if (typeof memberId === "string" && memberId.length > 0) {
                contactIdSet.add(memberId);
              }
            }
          }

          for (const community of communities as any[]) {
            if (!community || typeof community !== "object") continue;
            const members = Array.isArray(community.members) ? community.members : [];
            for (const member of members) {
              const memberId =
                typeof member === "string"
                  ? member
                  : member?.userId || member?.userID || member?.user_id;
              if (typeof memberId === "string" && memberId.length > 0) {
                contactIdSet.add(memberId);
              }
            }
          }

          contactIdSet.delete(userId);
          const contactIds = Array.from(contactIdSet);

          const backupData = {
            apiToken: state.apiToken,
            userId,
            username,
            discriminator,
            deviceId,
            publicKey: state.keyPair.base64.publicKey,
            secretKey: state.keyPair.base64.secretKey,
            contactIds,
            timestamp: Date.now(),
          };

          // Encrypt using switch-core
          const { recovery: switchRecovery } = await import("@/core_bridge/module_api");
          const backupDataJson = JSON.stringify(backupData);
          console.log("🔐 [recovery] Encrypting backup data...");
          const encryptResult = switchRecovery.encryptBackup(state.mnemonic, backupDataJson);
          const { encryptedData, nonce, recoveryId } = encryptResult;

          if (!encryptedData || !nonce || !recoveryId) {
            console.error("❌ Encryption result is incomplete!", encryptResult);
            throw new Error("Encryption failed: missing required fields");
          }

          // Update auth keypair with derived recovery ID
          store.setAuthKeyPair({
            publicKey: recoveryId,
            privateKey: "",
          });

          // Upload to server
          console.log("📤 [recovery] Uploading backup to server...");
          const response = await coreRecovery.storeBackup(
            recoveryId,
            encryptedData,
            nonce,
          );
          if (!response.success) {
            throw new Error("Failed to store recovery backup");
          }
          console.log("✓ [recovery] Backup uploaded successfully");

          // Store mnemonic locally
          localStorage.setItem("account_mnemonic", state.mnemonic);
          localStorage.setItem("auth_keypair", JSON.stringify({ publicKey: recoveryId, privateKey: "" }));
          try {
            await stateServices.sessionState.setRecoveryPassphrase(state.mnemonic);
          } catch {
            // ignore
          }
          store.setIsSetupComplete(true);
          store.setIsAuthKeyRegistered(true);
          store.setSetupStep("complete");
        } catch (error) {
          console.error("[recovery] completeSetup error:", error);
          store.setRecoveryError("Failed to complete setup");
          store.setSetupStep("idle");
        }
      },
      showRecoveryPhrase: () => {
        const state = useStore.getState();
        if (state.mnemonic && state.isSetupComplete) {
          store.setSetupStep("displaying");
          store.setRecoveryError(null);
        }
      },
      hideRecoveryPhrase: () => {
        const state = useStore.getState();
        if (state.isSetupComplete) {
          store.setSetupStep("idle");
        }
      },
      clearRecoveryState: () => {
        store.setMnemonic(null);
        store.setAuthKeyPair(null);
        store.setIsRecovering(false);
        store.setRecoveryStep("idle");
        store.setRecoveryError(null);
        store.setEncryptedData(null);
        store.setIsDataRestored(false);
        store.setSetupStep("idle");
        store.setVerificationWords(null);
        store.setRegistrationError(null);
      },
      recoverAccount: async (mnemonic: string) => {
        try {
          store.setRecoveryError(null);
          store.setIsRecovering(true);
          store.setRecoveryStep("generating");

          await initSwitchCore();
          if (!coreRecovery.validateMnemonic(mnemonic)) {
            throw new Error("Invalid recovery phrase");
          }

          store.setRecoveryStep("verifying");
          const recoveryId = coreRecovery.deriveRecoveryId(mnemonic);
          const response = await coreRecovery.getBackup(recoveryId, appConfig.appId);
          if (!response.success) {
            throw new Error("Backup not found");
          }
          const encryptedData = (response as any).encrypted_data;
          const nonce = (response as any).nonce;
          if (!encryptedData || !nonce) {
              throw new Error("Missing backup data");
            }

            const decryptedJson = coreRecovery.decryptBackup(mnemonic, encryptedData, nonce);
            const payload = JSON.parse(decryptedJson || "{}");
            const contactIds = Array.isArray(payload?.contactIds)
              ? payload.contactIds.filter((id: unknown) => typeof id === "string" && id.length > 0)
              : [];
            const required = ["apiToken", "userId", "username", "discriminator", "deviceId", "publicKey", "secretKey"];
            for (const key of required) {
              if (!payload?.[key] || typeof payload[key] !== "string") {
                throw new Error("Invalid backup payload");
              }
            }

            // @platform-only: thin binding call — the challenge/verify proof-of-possession
            // logic lives in core::api::restore_session. We just pass the mnemonic (not the
            // recoveryId) so core can derive the recovery_id and run the round trip on-device;
            // the mnemonic is never transmitted.
            const restoreSessionResponse = await coreRecovery.restoreSession(mnemonic, appConfig.appId);
            if (!restoreSessionResponse?.success || !restoreSessionResponse?.token) {
              throw new Error("Failed to restore session");
            }
            const restoredToken = restoreSessionResponse.token;
            const restoredDiscriminator =
              restoreSessionResponse.discriminator || payload.discriminator;
            const restoredDeviceId =
              restoreSessionResponse.device_id || payload.deviceId;

            // Persist session + keys
            await stateServices.sessionState.setToken(restoredToken);
            await stateServices.sessionState.setDiscriminator(restoredDiscriminator);
            await stateServices.sessionState.setRecoveryPassphrase(mnemonic);
            try {
              localStorage.setItem("deviceId", restoredDeviceId);
            } catch {
              // ignore
            }

          await services.db.updateKeyPair(payload.publicKey, payload.secretKey);
          store.setKeyPair({
            cryptoKeys: {
              publicKey: decodeBase64(payload.publicKey),
              secretKey: decodeBase64(payload.secretKey),
            },
            base64: {
              publicKey: payload.publicKey,
              secretKey: payload.secretKey,
              },
            });

            store.setApiToken(restoredToken);
            store.setCommunityToken(restoredToken);
            setToken(restoredToken);

            const authKeyPair = { publicKey: recoveryId, privateKey: "" };
            store.setMnemonic(mnemonic);
          store.setAuthKeyPair(authKeyPair);
            localStorage.setItem("account_mnemonic", mnemonic);
            localStorage.setItem("auth_keypair", JSON.stringify(authKeyPair));

            const restoredLists = await services.db.replaceMembershipLists({
              contactIds,
              groupThreadIds: [],
              communityIds: [],
            });
            store.clearUsers();
            store.clearGroups();
            store.clearCommunities();
            store.setUsers(restoredLists.contacts as any);
            store.setGroups(restoredLists.groups as any);
            store.setCommunities(restoredLists.communities as any);

            store.setIsDataRestored(true);
            store.setIsSetupComplete(true);
            store.setIsAuthKeyRegistered(true);
            store.setRecoveryStep("complete");

            resetInitState();
            await runBootstrap(restoredToken);

            // Peer-assisted recovery: ask each contact to re-share groups and
            // communities we share. See web/src/features/recovery/recoveryAnnounce.ts
            store.startRecoveryAnnounceWindow();
            const { broadcastRecoveryAnnounce } = await import("@/features/recovery/recoveryAnnounce");
            await broadcastRecoveryAnnounce(contactIds, payload.publicKey);
          } catch (error) {
            console.error("[recovery] recoverAccount error:", error);
            store.setRecoveryError("Failed to restore from backup");
          store.setRecoveryStep("error");
          throw error;
        } finally {
          store.setIsRecovering(false);
        }
      },
      deleteRecoveryBackup: async (recoveryId: string) => {
        try {
          store.setRecoveryError(null);
          console.log("🗑️ [recovery] Deleting backup from server...");
          const response = await coreRecovery.deleteBackup(recoveryId);
          if (!response.success) {
            throw new Error("Failed to delete recovery backup");
          }
          console.log("✓ [recovery] Backup deleted successfully");
          return { success: true };
        } catch (error) {
          console.error("[recovery] deleteRecoveryBackup error:", error);
          store.setRecoveryError("Failed to delete recovery backup");
          throw error;
        }
      },
    },

    // Credit actions (namespaced)
    credit: {
      setCreditHistoryOnMonth: store.setCreditHistoryOnMonth,
      addCreditHistoryEntry: store.addCreditHistoryEntry,
      setBalance: store.setBalance,
      setIap: store.setIap,
      clearCreditHistory: store.clearCreditHistory,
    },

    // LLM actions (namespaced)
    llm: {
      setLlmInput: store.setLlmInput,
      setLlmMessages: store.setLlmMessages,
      addLlmThread: store.addLlmThread,
      addMessageToThread: store.addMessageToThread,
      setLlmWaiting: store.setLlmWaiting,
      clearLlmMessages: store.clearLlmMessages,
      saveLlmMessages: store.saveLlmMessages,
      loadLlmMessages: store.loadLlmMessages,
    },

    // Registration actions (minimal placeholder)
    registration: {
      register: async ({ username }: { username: string }) => {
        const attempt = async (alreadyRetried = false): Promise<{ token: string }> => {
          try {
            console.info("[registration] register user (core API)", { username });
            // Ensure WASM is initialized before calling api
            await initSwitchCore();
            // Configure base URL/token if available
            if (typeof coreApi.configure === "function") {
            coreApi.configure(appConfig.apiUrl, useStore.getState().apiToken);
          }
          // Ensure we have a public key; generate if missing
          let publicKeyBase64 = useStore.getState().keyPair?.base64?.publicKey;
          if (!publicKeyBase64) {
            const kp = coreCrypto.boxKeypair();
            // Encode Uint8Array to base64
            publicKeyBase64 = btoa(String.fromCharCode(...kp.public_key));
          }

          const result = await coreApi.register(
            username,
            appConfig.appId,
            publicKeyBase64,
          );
          console.info("[registration] register result", result);
          const token = (result as any)?.token || (result as any)?.Token || result;
          if (!token) {
            const errMsg = (result as any)?.error || "Registration did not return a token";
            console.error("[registration] register failed (no token)", result);
            throw new Error(errMsg);
          }
          store.setApiToken(token);
          store.setCommunityToken(token);
          // Kick off WebSocket connect after registering
          setToken(token);
          initializeWebSocket?.();
          return { token };
        } catch (err: any) {
            console.error("[registration] register threw", err);
            let serverMsg = "";
            let retryAfterMs: number | undefined;
            let parsed: any;
            // Fallback: if response object exists but no text() helper (core errors)
            if (!err?.response && err?.toString) {
              serverMsg = err.toString();
            }
            if (err?.response?.text) {
              try {
                serverMsg = await err.response.text();
                console.error("[registration] server response text", serverMsg);
                parsed = JSON.parse(serverMsg);
                serverMsg = parsed?.message || parsed?.error || serverMsg;
                if (parsed?.retry_after) {
                  retryAfterMs = Number(parsed.retry_after) * 1000;
                }
              } catch (e) {
                console.warn("Failed to read error response text", e);
              }
            }
            const msg =
              err?.message ||
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              serverMsg ||
              "Registration failed";

            // If rate-limited in dev, retry once after retry_after or short delay
            if (
              !alreadyRetried &&
              (parsed?.error === "rate_limited" || msg.toLowerCase().includes("too many")) &&
              import.meta.env.DEV
            ) {
              const delay = retryAfterMs && retryAfterMs > 0 ? retryAfterMs : 2000;
              console.warn(
                `[registration] rate limited, retrying once after ${delay}ms (dev only)`,
              );
              await new Promise((resolve) => setTimeout(resolve, delay));
              return attempt(true);
            }

            throw new Error(msg);
          }
        };

        return attempt();
      },
    },

    // Tab selection helper
    selectTab: ({ tab }: { tab: string }) => store.setActiveTab(tab as any),

    // Router helper - navigates back
    routerBack: () => services.router.back(),

    // Display photo action - picks and uploads a profile photo
    uploadDisplayPhotoBlob: async (blob: Blob, mimeType?: string) => {
      const uploadToPresigned = async (url: string, file: Blob, contentType?: string) => {
        await fetch(url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": contentType || "application/octet-stream",
            "x-amz-acl": "public-read",
          },
        });
      };

      await initSwitchCore();
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Encrypt and get presigned URL from core
      const prepared = await coreProfile.prepareDisplayPhotoUpload(bytes);
      const myUserId = (store as any).myUserId || parseJwt(store.apiToken || "").user_id;
      if (!myUserId) {
        throw new Error("Cannot upload display photo without user id");
      }

      // Upload encrypted blob (must set ACL per server expectations)
      await uploadToPresigned(
        prepared.uploadUrl,
        new Blob([prepared.encryptedData], { type: prepared.mimeType }),
        prepared.mimeType
      );

      // Store clean URL via core API and save key locally for decryption
      await coreApi.setDisplayPhotoWithMedia({
        url: prepared.cleanUrl,
        keyBase64: prepared.keyBase64,
        mimeType: prepared.mimeType,
      });
      store.setDisplayPhotoKey(prepared.keyBase64);
      const profileEntry = {
        userId: myUserId,
        url: prepared.cleanUrl,
        keyBase64: prepared.keyBase64,
      };
      // Persist profile info for reloads
      try {
        const { updateProfile } = await import("@/services/module_api");
        await updateProfile(
          JSON.stringify({
            userId: myUserId,
            displayPhoto: prepared.cleanUrl,
            displayPhotoMedia: {
              url: prepared.cleanUrl,
              keyBase64: prepared.keyBase64,
              mimeType: prepared.mimeType,
            },
          })
        );
        try {
          localStorage.setItem("displayPhotoUrl", prepared.cleanUrl);
        } catch {
          // ignore storage errors
        }
      } catch (err) {
        console.warn("[compat] failed to persist profile json", err);
      }
      // Cache decrypted blob locally (preferred rendering path)
      try {
        await cacheDecryptedPhoto({
          ...profileEntry,
          blob,
        });
      } catch (err) {
        console.warn("[compat] failed to cache decrypted photo", err);
      }
      // Persist key to storage for reloads
      try {
        const { saveDisplayPhotoKey } = await import("@/services/module_api");
        await saveDisplayPhotoKey(prepared.keyBase64);
        try {
          localStorage.setItem("displayPhotoKey", prepared.keyBase64);
        } catch {
          // ignore
        }
      } catch (err) {
        console.warn("[compat] failed to persist display photo key", err);
      }
      // Fan out new display photo key/url to all contacts (best-effort) via core helper
      try {
        const secretKey =
          store.keyPair?.base64?.secretKey || (store.keyPair as any)?.base64?.secret_key || "";
        if (secretKey) {
          const contacts = Object.values(store.contactsById || {}) as any[];
          for (const contact of contacts) {
            const receiverId = contact?.userId;
            const receiverPublicKey = resolveContactPublicKey(contact);
            if (!receiverId || receiverId === myUserId || !receiverPublicKey) continue;
            try {
              const preparedFanout = (coreApi as any).prepareDisplayPhotoFanout?.(
                myUserId,
                prepared.cleanUrl,
                prepared.keyBase64,
                receiverId,
                receiverPublicKey,
                myUserId,
                secretKey
              );
              if (preparedFanout) {
                const threadKey = preparedFanout.thread_key || createDirectThreadKey(myUserId, receiverId);
                // Send without local insert/UI noise; transport still owns event boundary checks.
                sendWebSocket(WebSocketEventType.MESSAGE_SEND, {
                  id: preparedFanout.id,
                  encryptedContent: preparedFanout.encrypted_content || preparedFanout.encryptedContent,
                  nonce: preparedFanout.nonce,
                  contentType: preparedFanout.content_type || "SYSTEM",
                  threadKey,
                  receiverId,
                });
              }
            } catch (err) {
              console.warn("[compat] fanout display photo key failed for", receiverId, err);
            }
          }
        }
      } catch (err) {
        console.warn("[compat] display photo fanout skipped", err);
      }
      // displayPhoto and myProfilePhoto were set to local cache above (if available)
      store.incrementProfileRenderRef?.();

      console.log("[compat] uploadDisplayPhotoBlob: updated successfully");
      // TODO: deliver prepared.keyBase64 to other devices via messaging
    },

    setDisplayPhoto: async () => {
      console.warn("[compat] setDisplayPhoto: Opening file picker");
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        console.log("[compat] setDisplayPhoto: File selected", file.name);

        try {
          // Simple center-crop fallback for legacy flows
          const cropToSquare = async (sourceFile: File, size = 512): Promise<Blob> => {
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(sourceFile);
            });
            const img = new Image();
            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error("Image load failed"));
              img.src = dataUrl;
            });

            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("Canvas not supported");
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (b) => (b ? resolve(b) : reject(new Error("Failed to crop image"))),
                sourceFile.type || "image/jpeg",
                0.92
              );
            });
            return blob;
          };

          const croppedBlob = await cropToSquare(file);
          // Prefer new blob upload path; if missing, fall back to legacy
          if ((store as any).uploadDisplayPhotoBlob) {
            await (store as any).uploadDisplayPhotoBlob(croppedBlob, file.type);
          } else {
            // inline legacy upload if helper not present
            const arrayBuffer = await croppedBlob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            await initSwitchCore();
            const prepared = await coreProfile.prepareDisplayPhotoUpload(bytes);
            await fetch(prepared.uploadUrl, {
              method: "PUT",
              body: new Blob([prepared.encryptedData], { type: prepared.mimeType }),
              headers: {
                "Content-Type": prepared.mimeType,
                "x-amz-acl": "public-read",
              },
            });
            await coreApi.setDisplayPhotoWithMedia({
              url: prepared.cleanUrl,
              keyBase64: prepared.keyBase64,
              mimeType: prepared.mimeType,
            });
            (store as any).setProfilePhoto?.({
              userId: (store as any).myUserId || parseJwt(store.apiToken || "").user_id,
              url: prepared.cleanUrl,
              keyBase64: prepared.keyBase64,
            });
            const { updateProfile } = await import("@/services/module_api");
            await updateProfile(
              JSON.stringify({
                userId: (store as any).myUserId || parseJwt(store.apiToken || "").user_id,
                displayPhoto: prepared.cleanUrl,
                displayPhotoMedia: {
                  url: prepared.cleanUrl,
                  keyBase64: prepared.keyBase64,
                  mimeType: prepared.mimeType,
                },
              })
            );
            store.setDisplayPhotoKey(prepared.keyBase64);
            if (store.setMyProfilePhoto) {
              store.setMyProfilePhoto(prepared.cleanUrl);
            }
          }
        } catch (err) {
          console.error("[compat] setDisplayPhoto failed", err);
        }
      };
      input.click();
    },

    // Push notification registration
    registerPlatformPushToken: async () => {
      console.log("[compat] registerPlatformPushToken: Starting push registration");

      // Check browser support
      if (!("Notification" in window)) {
        throw new Error("Notifications not supported in this browser");
      }
      if (!("serviceWorker" in navigator)) {
        throw new Error("Service workers not supported in this browser");
      }
      if (!("PushManager" in window)) {
        throw new Error("Push notifications not supported in this browser");
      }

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }
      console.log("[compat] Notification permission granted");

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      console.log("[compat] Service worker ready");

      // Convert VAPID key from base64 to Uint8Array
      const vapidKey = appConfig.publicVapidKey;
      const urlBase64ToUint8Array = (base64String: string) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      console.log("[compat] Push subscription created:", subscription);

      // Get or generate device ID
      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId = generateUUID();
        localStorage.setItem("deviceId", deviceId);
      }

      // Send subscription to server
      const apiToken = useStore.getState().apiToken;
      if (!apiToken) {
        throw new Error("Not authenticated - cannot register push token");
      }

      const response = await fetch(`${appConfig.apiUrl}/register-platform-push-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: buildBasicAuthorizationHeader(apiToken),
        },
        body: JSON.stringify({
          deviceId,
          token: JSON.stringify(subscription.toJSON()),
          platform: "web",
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to register push token: ${errorText}`);
      }

      console.log("[compat] Push token registered with server successfully");
      return { success: true, deviceId };
    },

    // Attachment helpers
    attachment: {
      openAttachment: async (payload?: {
        id?: string;
        url?: string;
        objectUrl?: string;
        encryptedUrl?: string;
        keyBase64?: string;
        mimeType?: string;
        filename?: string;
        attachment?: {
          id?: string;
          url?: string;
          objectUrl?: string;
          encryptedUrl?: string;
          keyBase64?: string;
          mimeType?: string;
          filename?: string;
        };
      }) => {
        const attachmentPayload = payload?.attachment || payload;
        const encryptedObjectUrl = attachmentPayload?.objectUrl || attachmentPayload?.encryptedUrl || attachmentPayload?.url;
        if (!encryptedObjectUrl) {
          console.warn("[attachment] openAttachment: no URL provided");
          return;
        }

        try {
          const { attachments } = await import("@/core_bridge/module_api");
          const { filesystem } = await import("@/services/module_api");

          const sanitizePathPart = (value: string): string =>
            value.trim().replace(/[^A-Za-z0-9._-]/g, "_").replace(/^[._-]+|[._-]+$/g, "") || "attachment";
          const attachmentId = sanitizePathPart(attachmentPayload?.id || "unknown");
          const filename = sanitizePathPart(attachmentPayload?.filename || "download");
          const cachePath = `attachments/${attachmentId}/${filename}`;
          const openBlob = (blob: Blob) => {
            const blobUrl = URL.createObjectURL(blob);
            if (
              attachmentPayload?.mimeType?.startsWith("image/") ||
              attachmentPayload?.mimeType?.startsWith("video/") ||
              attachmentPayload?.mimeType?.startsWith("audio/")
            ) {
              window.open(blobUrl, "_blank");
            } else {
              const a = document.createElement("a");
              a.href = blobUrl;
              a.download = attachmentPayload?.filename || "download";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
          };

          const cachedBlob = await filesystem.readFileToBlob?.({ path: cachePath });
          if (cachedBlob) {
            console.log("[attachment] opened cached file:", { cachePath });
            openBlob(cachedBlob);
            return;
          }

          let baseUrl = encryptedObjectUrl;
          let keyBase64 = attachmentPayload?.keyBase64;
          if (!keyBase64) {
            [baseUrl, keyBase64] = attachments.parseUrl(encryptedObjectUrl);
          }

          console.log("[attachment] downloading:", { baseUrl, hasKey: !!keyBase64 });

          // Fetch encrypted data
          const response = await fetch(baseUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.statusText}`);
          }
          const encryptedData = new Uint8Array(await response.arrayBuffer());

          // Decrypt
          const decrypted = attachments.decrypt(encryptedData, keyBase64);

          const blob = new Blob([decrypted], { type: attachmentPayload?.mimeType || "application/octet-stream" });
          await filesystem.writeFile({ path: cachePath, blob });
          openBlob(blob);

          console.log("[attachment] opened successfully");
        } catch (err) {
          console.error("[attachment] open failed:", err);
        }
      },
      onSelectedFile: async (_file?: any) => {
        console.warn("[compat/attachment] onSelectedFile stub invoked");
      },
    },

    // Thread helpers
    selectThread: ({
      threadKey,
      receiverId,
      senderId,
      groupId,
      title,
    }: {
      threadKey?: string;
      receiverId?: string;
      senderId?: string;
      groupId?: string;
      title?: string;
    } = {}) => {
      // Map legacy args into selectedThread shape
      const id =
        threadKey ||
        groupId ||
        receiverId ||
        senderId ||
        undefined;
      if (!id) {
        store.clearSelectedThread();
        return;
      }
      const label = title || "";
      store.setSelectedThread({
        threadKey: id,
        receiverId,
        senderId,
        groupId,
        title: label,
      } as any);
    },

    // Logout action - clears state and database
    logout: async () => {
      await performClientLogout("user_requested");
    },
  };
  }, []); // Empty dependency array - actions are stable and don't change
}

/**
 * Compatibility hook for useEffects
 * Returns services
 */
export function useEffects() {
  return {
    ...services,
    // api shim for AnonRegistrationScreen
    api: {
      create_anon_account: async () => {
        console.warn("[compat] create_anon_account not implemented");
        throw new Error("create_anon_account not available");
      },
    },
  };
}
